import { ImageLoaderProps } from 'next/image'
import getConfig from 'next/config'

import type { SearchFormConfig } from '@awaze/design-system.compositions.search-form'
import { getOptimizelyUserId } from '@awaze/mfe-common.lib.optimizely'
import { parseForceDecisionsFromUrlQuery } from '@awaze/mfe-common.lib.optimizely/dist/utils/parseForceDecisionsFromUrlQuery'
import type {
  SearchCriteria,
  SearchFormCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { isBotFromHeaders } from '@awaze/mfe-common.utils.is-bot-from-headers'
import { IncomingHttpHeaders } from 'http'
import { NextApiRequest, NextApiResponse } from 'next'
import { GRADE_CAT_IDS } from 'src/constants'
import { useAppConfig } from 'src/context/app-config-provider'

interface CleanObjectOf<DirtyData> {
  data: DirtyData
  filterOutValues: Array<null | undefined | '' | 0>
}

export const cleanObjectOf = <DirtyObject>({
  data,
  filterOutValues,
}: CleanObjectOf<DirtyObject>): Partial<DirtyObject> =>
  typeof data === 'object'
    ? Object.entries(data)
        .filter(([, value]) => !filterOutValues.includes(value))
        .reduce((obj, [key, value]) => {
          obj[key] = value
          return obj
        }, {})
    : {}

export function createSlugFromString(
  name: string,
  code: string | number,
): string {
  const formattedName = name
    .replace(/ - /g, '-')
    .replace(/ /g, '-')
    .replace(/'/g, '')
    .replace(/&rsquo;/g, '')
    .replace(/[^\w\\-]/g, '')
  const path = `${formattedName}-${code}`
  return path.toLowerCase()
}

export function createIdFromString(str: string): string {
  return str.toLowerCase().replace(/ /g, '-')
}

export const createInitialValuesFromConfig = (
  searchFormConfig: SearchFormConfig,
): SearchCriteria => {
  const {
    accommodationType,
    adults,
    children,
    dates,
    destination,
    destinationId,
    destinationURL,
    duration,
    flexibility,
    infants,
    pets,
  } = searchFormConfig

  const configWithoutOptional = {
    destination: destination.defaultValue,
    destinationURL: destinationURL.defaultValue,
    destinationId: destinationId.defaultValue,
    ...(adults.max > 0 && { adult: adults.defaultValue }),
    ...(children.max > 0 && { child: children.defaultValue }),
    ...(infants.max > 0 && { infant: infants.defaultValue }),
    ...(pets.max > 0 && { pets: pets.defaultValue }),
    start: dates.defaultValue,
    range: Number(flexibility.defaultValue),
    nights: Number(duration.defaultValue),
    ...(accommodationType?.defaultValue && {
      accommodationType: accommodationType.defaultValue,
    }),
  } as SearchCriteria

  return configWithoutOptional
}

export const capitalise = (word: string): string => {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

export function formatPropertyPageURL(
  domain: string,
  product: string,
  name: string,
  code: string | number,
  queryString: string,
): string {
  return `${domain}/${product}/${createSlugFromString(
    name,
    code,
  )}?${queryString}`
}

export function generatePageTitle(brand: string, location?: string): string {
  if (location) {
    return `${capitalise(brand)} | Book your accommodation in ${capitalise(
      location,
    )} now`
  }
  return `${capitalise(brand)} | Book your accommodation in all regions now`
}

export function generatePageDescription(
  brand: string,
  location?: string,
): string {
  if (location) {
    return `Find hand-picked holiday cottages in ${capitalise(
      location,
    )} at ${capitalise(brand)}. Compare cottages or book online.`
  }
  return `Find hand-picked holiday cottages in All Regions at ${capitalise(
    brand,
  )}. Compare cottages or book online.`
}

export function getAssetUrl(path: string): string {
  const { assetPrefix } = getConfig()
  return assetPrefix ? `${assetPrefix}/public${path}` : `${path}`
}

export function getPublicAssetUrl(path: string): string {
  const { ASSET_PREFIX } = useAppConfig()
  return ASSET_PREFIX ? `${ASSET_PREFIX}/public${path}` : `${path}`
}

export function getImageAssetUrl({ src, width }: ImageLoaderProps): string {
  const imageOpts = `${src}?w=${width}`

  return getAssetUrl(imageOpts)
}

export function scrollTo(element: Element): void {
  element && element.scrollIntoView({ behavior: 'smooth' })
}

export function setCookie(
  cname: string,
  cvalue: number | string,
  cexpires?: string,
): void {
  if (typeof window === 'undefined') return
  const expiresString = cexpires ? 'expires=' + cexpires : ''
  document.cookie = `${cname} = ${cvalue}; path=/; ${expiresString}`
}

export function getCookie(cname: string): string | null {
  if (typeof window === 'undefined') return null
  const cookie = document.cookie.match('(^|;) ?' + cname + '=([^;]*)(;|$)')
  return cookie ? cookie[2] : null
}

export function getEndOfDayAsUTCString(): string {
  const today = new Date()
  const midnight = new Date(
    Date.UTC(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
    ),
  )
  return midnight.toUTCString()
}

export function calculateTotalPages(total: number, pageSize: number): number {
  return Math.ceil(total / pageSize)
}

export const getFilterTypeArray = (type: string): Array<string> => {
  return type ? [type] : []
}

export function calculateSelectedFilters(
  selectedFilters: SearchCriteria,
): number {
  let total = 0

  Object.keys(selectedFilters).forEach((selectedFilter) => {
    if (Array.isArray(selectedFilters[selectedFilter])) {
      total += selectedFilters[selectedFilter].length
    } else {
      total += getFilterTypeArray(selectedFilters[selectedFilter]).length
    }
  })

  return total
}

export function getMinMaxRating(rating: string): Array<string> {
  const minMaxRating = rating.split('-')
  minMaxRating[1] =
    parseInt(minMaxRating[1]) > 9 ? '10' : `${minMaxRating[1]}.9`

  return minMaxRating
}

export const mapGradeParamToNumber = (grade: string): string | null => {
  return (
    Object.keys(GRADE_CAT_IDS).find(
      (key) => GRADE_CAT_IDS[Number(key)] === grade,
    ) || null
  )
}

export const setBackButtonConfig = (): void => {
  if (typeof window !== 'undefined') {
    const url = window.location.href
    localStorage.setItem('lastSearchData', `Search MFE,${url}`)
    localStorage.setItem('lastSearchFeatures', '')
    setCookie('userSearch', url)
  }
}

export type OldBrand =
  | 'agnostic'
  | 'awaze'
  | 'cottages.com'
  | 'dansommer'
  | 'fincallorca'
  | 'hoseasons'
  | 'jamesVillas'
  | 'novasol'

export const getHeaderString = <T>(
  headers: IncomingHttpHeaders,
  headerName: string,
  defaultReturn: T,
): string | T => {
  const lookupValue = headers[headerName]

  if (lookupValue === undefined) {
    return defaultReturn
  }

  return Array.isArray(lookupValue) ? lookupValue[0] : lookupValue
}

export const getOptimizelyHeadersForOneSearch = (
  req: NextApiRequest,
  res: NextApiResponse,
): Record<string, string> => {
  const userId = getOptimizelyUserId({
    cookies: req.cookies,
    response: res,
  })

  const forcedDecisions = parseForceDecisionsFromUrlQuery(req.query)

  const forcedDecisionString =
    forcedDecisions.length > 0
      ? `${forcedDecisions[0].flagKey}:${forcedDecisions[0].variationKey}`
      : ''

  const isBot = isBotFromHeaders(
    req.headers['user-agent'] || '',
    (req.headers['akamai-bot'] as string) ?? null,
    (req.headers['x-amzn-waf-bot-category'] as string) ?? null,
    (req.headers['x-abuse-info'] as string) ?? null,
  )

  return {
    'x-awaze-user-agent': req.headers['user-agent'] || '',
    'x-awaze-bot-traffic': isBot ? 'true' : 'false',
    ...(userId && { 'x-awaze-opti-id': userId }),
    ...(forcedDecisionString !== '' && {
      'x-force-experiment-variant': `${forcedDecisionString}`,
    }),
  }
}

export const searchCriteriaComparisonString = (
  stateSearchCriteria: SearchFormCriteria,
): string => JSON.stringify(stateSearchCriteria)

export const parseSetCookie = (str: string): Record<string, string> =>
  str
    .split(';')
    .map((v) => v.split('='))
    .reduce<Record<string, string>>((acc, v) => {
      !v[1]
        ? (acc[decodeURIComponent(v[0].trim())] = 'true')
        : (acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(
            v[1].trim(),
          ))
      return acc
    }, {})
